/**
 * Locale-aware formatting hook
 *
 * Provides number, date, and relative time formatting
 * that respects the current i18next language setting.
 *
 * IMPORTANT: For UI chrome only. Evidence data and forensic
 * timestamps must NOT use these formatters.
 */

import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface Formatters {
  formatNumber: (value: number, options?: Intl.NumberFormatOptions) => string
  formatDate: (value: Date | string, options?: Intl.DateTimeFormatOptions) => string
  formatRelativeTime: (value: Date | string) => string
}

const RELATIVE_TIME_UNITS: Array<{
  unit: Intl.RelativeTimeFormatUnit
  divisor: number
}> = [
  { unit: 'year', divisor: 31_536_000_000 },
  { unit: 'month', divisor: 2_592_000_000 },
  { unit: 'week', divisor: 604_800_000 },
  { unit: 'day', divisor: 86_400_000 },
  { unit: 'hour', divisor: 3_600_000 },
  { unit: 'minute', divisor: 60_000 },
  { unit: 'second', divisor: 1000 },
]

export function useFormatters(): Formatters {
  const { i18n } = useTranslation()
  const locale = i18n.language

  return useMemo(() => {
    const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string => {
      return new Intl.NumberFormat(locale, options).format(value)
    }

    const formatDate = (value: Date | string, options?: Intl.DateTimeFormatOptions): string => {
      const date = typeof value === 'string' ? new Date(value) : value
      return new Intl.DateTimeFormat(locale, options).format(date)
    }

    const formatRelativeTime = (value: Date | string): string => {
      const date = typeof value === 'string' ? new Date(value) : value
      const diff = date.getTime() - Date.now()
      const absDiff = Math.abs(diff)

      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })

      for (const { unit, divisor } of RELATIVE_TIME_UNITS) {
        if (absDiff >= divisor) {
          const amount = Math.round(diff / divisor)
          return rtf.format(amount, unit)
        }
      }

      return rtf.format(0, 'second')
    }

    return { formatNumber, formatDate, formatRelativeTime }
  }, [locale])
}
